import { BulkDeleteDialog, useBulkDelete } from '@/components/app/bulk-delete';
import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  type ConversationListItem,
  useArchiveConversation,
  useDeleteConversation,
} from '@/features/conversations/api';
import { Archive, MoreHorizontal, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

const nameOf = (c: ConversationListItem) =>
  c.externalUserName ?? c.externalUserId;

/**
 * Selection, archive and delete for the desktop inbox list: the row
 * checkboxes, the select-all box beside the search, and the "⋯" menu on each
 * row, which acts on the whole selection when opened on a selected row.
 *
 * Delete reuses the shared bulk-delete flow (confirm dialog, typed
 * confirmation for big batches, partial-failure toast). Archive is not
 * destructive — the thread stays under All — so it runs without a dialog.
 */
export function useInboxRowActions(
  conversations: ConversationListItem[],
  /** Clears the selection when the visible rows change (tab, filter, search). */
  resetKey: string,
  /**
   * Archive is offered on From Ads, Needs Reply and Cold. In Conversation is
   * live work, and All is where archived threads go — the menu there is
   * Delete alone.
   */
  canArchive: boolean
) {
  const { deleteConversationAsync: deleteQuietly } = useDeleteConversation({
    showSuccessToast: false,
    showErrorToast: false,
  });
  const { deleteConversation, isDeleting: isDeletingOne } =
    useDeleteConversation();
  const { setArchived } = useArchiveConversation();
  const { setArchivedAsync: setArchivedQuietly } = useArchiveConversation({
    showToasts: false,
  });

  const rowIds = useMemo(() => conversations.map((c) => c.id), [conversations]);
  const byId = useMemo(
    () => new Map(conversations.map((c) => [c.id, c])),
    [conversations]
  );

  const bulk = useBulkDelete({
    deleteOne: deleteQuietly,
    noun: { singular: 'conversation', plural: 'conversations' },
    rowIds,
    labelFor: (id) => {
      const c = byId.get(id);
      return c ? nameOf(c) : undefined;
    },
    resetKey,
  });

  const [pendingDelete, setPendingDelete] =
    useState<ConversationListItem | null>(null);

  const allSelected = rowIds.length > 0 && bulk.count === rowIds.length;
  const someSelected = bulk.count > 0 && !allSelected;

  const toggleAll = (checked: boolean) =>
    bulk.selection.onSelectionChange(new Set(checked ? rowIds : []));

  const toggleOne = (id: string, checked: boolean) => {
    const next = new Set(bulk.selected);
    if (checked) next.add(id);
    else next.delete(id);
    bulk.selection.onSelectionChange(next);
  };

  // Archive or un-archive several threads with one summary toast. Anything
  // that failed stays selected so the same menu can retry it.
  const setArchivedMany = async (ids: string[], archived: boolean) => {
    const results = await Promise.allSettled(
      ids.map((id) => setArchivedQuietly({ id, archived }))
    );
    const failed = ids.filter((_, i) => results[i].status === 'rejected');
    const done = ids.length - failed.length;
    const verb = archived ? 'archived' : 'unarchived';
    if (done > 0) {
      toast.success(
        `${done} conversation${done === 1 ? '' : 's'} ${verb}${archived ? ' — still under All' : ''}`
      );
    }
    if (failed.length > 0) {
      toast.error(
        `${failed.length} conversation${failed.length === 1 ? '' : 's'} could not be ${verb}. ${failed.length === 1 ? 'It is' : 'They are'} still selected.`
      );
      bulk.selection.onSelectionChange(new Set(failed));
    } else {
      bulk.clear();
    }
  };

  const selectAll = (
    <Checkbox
      aria-label={
        allSelected ? 'Deselect all conversations' : 'Select all conversations'
      }
      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
      disabled={rowIds.length === 0}
      onCheckedChange={(checked) => toggleAll(checked === true)}
    />
  );

  const renderCheckbox = (c: ConversationListItem) => (
    <Checkbox
      aria-label={`Select conversation with ${nameOf(c)}`}
      checked={bulk.selected.has(c.id)}
      onCheckedChange={(checked) => toggleOne(c.id, checked === true)}
    />
  );

  /**
   * The row's "⋯" menu. Opened on a SELECTED row it acts on the whole
   * selection ("Archive 3 conversations"); on any other row, on that row
   * alone — so the menu is the one place every action lives, with no separate
   * selection toolbar.
   */
  const renderMenu = (c: ConversationListItem) => {
    const targets =
      bulk.selected.has(c.id) && bulk.count > 1
        ? conversations.filter((x) => bulk.selected.has(x.id))
        : [c];
    const many = targets.length > 1;
    const suffix = many ? ` ${targets.length} conversations` : '';
    // Needs Reply and Cold never list an archived thread, but a selection
    // can outlive a refetch: skip any that already are.
    const toArchive = targets.filter((t) => !t.isArchived);

    const onArchive = () => {
      if (!many) {
        setArchived({ id: c.id, archived: true });
        return;
      }
      void setArchivedMany(
        toArchive.map((t) => t.id),
        true
      );
    };

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={
              many
                ? `Actions for ${targets.length} selected conversations`
                : `Actions for conversation with ${nameOf(c)}`
            }
            className="size-8 text-muted-foreground data-[state=open]:bg-muted"
            size="icon"
            variant="ghost"
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {canArchive && toArchive.length > 0 ? (
            <>
              <DropdownMenuItem onSelect={onArchive}>
                <Archive className="size-4" />
                Archive{suffix}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : null}
          <DropdownMenuItem
            onSelect={() => (many ? bulk.openDialog() : setPendingDelete(c))}
            variant="destructive"
          >
            <Trash2 className="size-4" />
            Delete{suffix}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const dialogs = (
    <>
      <BulkDeleteDialog bulk={bulk} />
      <ConfirmDeleteDialog
        description="The conversation and its messages are removed from your inbox. This cannot be undone. To keep it but get it out of the way, archive it instead."
        isPending={isDeletingOne}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteConversation(pendingDelete.id, {
            onSettled: () => setPendingDelete(null),
          });
        }}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        open={pendingDelete !== null}
        title={
          pendingDelete
            ? `Delete the conversation with ${nameOf(pendingDelete)}?`
            : 'Delete conversation?'
        }
      />
    </>
  );

  return { selectAll, renderCheckbox, renderMenu, dialogs };
}
